import { useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import LeadModal from "@/components/marketing/LeadModal";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

/**
 * Generic vertical page used by Corporate / Malls / Education / Expressways.
 * The K-12 Schools vertical has its own dedicated page (the original Landing).
 */
export default function VerticalPage({ vertical, hero, blurb, palette, features, useCases, kpis }) {
  const [leadOpen, setLeadOpen] = useState(false);
  return (
    <div className="min-h-screen bg-cream-50 text-ink">
      <MarketingNav onCta={() => setLeadOpen(true)}/>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <Link to="/platform" data-testid="back-to-platform"
          className="inline-flex items-center gap-1 text-xs font-semibold text-ink-600 hover:text-brand">
          <ArrowLeft size={12}/> All verticals
        </Link>
        <div className={`inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-full ${palette.badge} text-[11px] font-bold uppercase tracking-[0.18em]`}>
          {vertical}
        </div>
        <h1 className="font-heading text-4xl sm:text-6xl font-extrabold tracking-tight mt-4 max-w-4xl leading-[1.05]">
          {hero}
        </h1>
        <p className="text-lg text-ink-600 mt-5 max-w-2xl leading-relaxed">{blurb}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button onClick={() => setLeadOpen(true)} data-testid="vertical-cta-demo"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ink text-white font-semibold hover:bg-ink-700">
            Request a demo <ArrowRight size={16}/>
          </button>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-border text-ink-700 font-semibold hover:border-ink">
            See the full platform
          </Link>
        </div>
        {kpis && kpis.length > 0 && (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpis.map(k => (
              <div key={k.label} className={`rounded-2xl p-5 ${palette.kpi}`}>
                <div className="font-heading text-3xl font-extrabold">{k.value}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mt-1">{k.label}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border-y border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">What's included</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-3">
            Built for the way you operate.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="p-6 rounded-2xl border border-cream-200 hover:border-ink/30 transition">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
                    <Icon size={20}/>
                  </div>
                  <h3 className="font-heading text-lg font-extrabold text-ink mt-4">{f.title}</h3>
                  <p className="text-sm text-ink-600 mt-2 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {useCases && useCases.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">Day-to-day on Grubpe</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-3">
            What it actually looks like.
          </h2>
          <div className="mt-8 space-y-3 max-w-3xl">
            {useCases.map((uc, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-cream-200 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5"/>
                <p className="text-sm text-ink-700 leading-relaxed">{uc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-brand text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            Let's tailor this for your {vertical.toLowerCase()}.
          </h2>
          <p className="text-white/80 mt-2 max-w-xl mx-auto">A 30-minute demo with our team, built around your kitchen and your vendors.</p>
          <button onClick={() => setLeadOpen(true)}
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-brand font-semibold hover:bg-cream-100">
            Request a demo <ArrowRight size={16}/>
          </button>
        </div>
      </section>

      <MarketingFooter/>
      <LeadModal open={leadOpen} onClose={() => setLeadOpen(false)} defaultVertical={vertical.toLowerCase()}/>
    </div>
  );
}
